/**
 * date-selekt component.
 *
 * The original <input> stays as the VALUE HOLDER (Postgres wire format,
 * visually hidden); the user sees a read-only .form-control displaying the
 * value in g.settings.dateFormat/timeFormat + a calendar/clock icon, and
 * picks from the dropdown panel. For ranges the library creates a second
 * hidden input (name = opts.endName or '<name>_to') carrying the end value;
 * an initial range can also be given in the holder as 'start,end'.
 *
 * Types: 'date' | 'datetime' | 'time', each with range: true/false.
 * min/max only disable USER selection - values set from code (holder value at
 * init, setValue()) are always accepted and displayed.
 *
 * Full reference: docs/libs/date-selekt.md.
 */
import { pad, toPgTimestamp, toPgTime, parsePgTimestamp, parsePgTime, formatDisplay, formatTimeDisplay, monthLabel, monthNamesShort, weekdayNames, sameDay, dayOnly, parseBound } from './dt.js';

const t = (s) => (window.g && window.g.translate ? window.g.translate(s) : s);
const lang = () => (window.g && window.g.lang) || 'en';
const setting = (key, fallback) => {
	const value = window.g && window.g.settings ? window.g.settings[key] : undefined;
	// 0 is a valid value (weekStart: Sunday) - only "not set" falls back
	return value === undefined || value === null || value === '' ? fallback : value;
};

let uidCounter = 0;

export class DateSelekt {
	constructor(el, opts) {
		this.el = el;
		this.opts = opts;
		this.uid = `dsel-${++uidCounter}`;
		this.hasDate = opts.type !== 'time';
		this.hasTime = opts.type !== 'date';
		this.range = !!opts.range;
		this.readonly = !!opts.readonly || el.readOnly;
		this.open = false;

		// Values: dates as Date, times as {h,m}; end used only for ranges.
		this.start = null;
		this.end = null;
		this.minDate = parseBound(opts.minDate);
		this.maxDate = parseBound(opts.maxDate);
		this.viewDate = dayOnly(new Date());
		this.viewMode = 'days'; // days | months | years (label click drills up)
		this.pending = false; // range: start picked, waiting for the end
		this.hoverDay = null;

		this.build();
		this.readInitial();
		this.syncHolder(false);
		this.lastFired = this.signature();
		el.dateSelekt = this;
	}

	// ---- setup -----------------------------------------------------------------

	build() {
		const el = this.el;
		this.wrap = document.createElement('div');
		this.wrap.className = `dsel dsel-type-${this.opts.type}${this.range ? ' dsel-range' : ''}${this.readonly ? ' dsel-readonly' : ''}`;
		el.parentNode.insertBefore(this.wrap, el);
		this.wrap.appendChild(el);
		el.classList.add('dsel-holder');
		el.tabIndex = -1;
		el.setAttribute('aria-hidden', 'true');

		if (this.range) {
			this.endInput = document.createElement('input');
			this.endInput.type = 'hidden';
			this.endInput.name = this.opts.endName || (el.name ? `${el.name}_to` : '');
			this.wrap.appendChild(this.endInput);
		}

		this.display = document.createElement('input');
		this.display.type = 'text';
		this.display.readOnly = true; // picking happens in the panel
		this.display.className = 'form-control dsel-display';
		['form-control-sm', 'form-control-lg', 'is-invalid', 'is-valid'].forEach((c) => {
			if (el.classList.contains(c)) this.display.classList.add(c);
		});
		this.display.placeholder = this.opts.placeholder || this.displayPattern();
		this.display.setAttribute('aria-haspopup', 'dialog');
		if (el.disabled) this.display.disabled = true;
		this.wrap.appendChild(this.display);

		this.clearBtn = document.createElement('span');
		this.clearBtn.className = 'dsel-clear';
		this.clearBtn.setAttribute('role', 'button');
		this.clearBtn.setAttribute('aria-label', t('Clear'));
		this.clearBtn.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
		this.clearBtn.addEventListener('click', (e) => { e.stopPropagation(); if (!this.readonly) this.clear(); });
		this.wrap.appendChild(this.clearBtn);

		const icon = document.createElement('span');
		icon.className = 'dsel-icon';
		icon.innerHTML = `<i class="${this.hasDate ? 'ri-calendar-line' : 'ri-time-line'}" aria-hidden="true"></i>`;
		this.wrap.appendChild(icon);

		this.panel = document.createElement('div');
		this.panel.className = 'dsel-panel';
		this.panel.hidden = true;
		this.wrap.appendChild(this.panel);

		const openIfAllowed = () => { if (!this.readonly && !this.el.disabled) this.openPanel(); };
		this.display.addEventListener('click', openIfAllowed);
		icon.addEventListener('click', openIfAllowed);
		this.display.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') this.closePanel();
			if ((e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') && !this.open) { e.preventDefault(); openIfAllowed(); }
		});

		this.onDocClick = (e) => { if (!this.wrap.contains(e.target)) this.closePanel(); };
	}

	// Trust the holder at init - even values outside min/max (set by code).
	readInitial() {
		const raw = String(this.el.value || '').trim();
		if (!raw) return;
		const parts = this.range ? raw.split(',').map((p) => p.trim()) : [raw];
		if (this.range && this.endInput.value) parts[1] = this.endInput.value;
		if (this.hasDate) {
			this.start = parsePgTimestamp(parts[0]);
			if (this.range) this.end = parsePgTimestamp(parts[1] || '');
		} else {
			this.start = parsePgTime(parts[0]);
			if (this.range) this.end = parsePgTime(parts[1] || '');
		}
		if (this.start && this.hasDate) this.viewDate = dayOnly(this.start);
	}

	displayPattern() {
		const dateFmt = this.opts.dateFormat || setting('dateFormat', 'DD.MM.YYYY');
		const timeFmt = this.opts.timeFormat || setting('timeFormat', 'HH:mm');
		let one = '';
		if (this.opts.type === 'date') one = dateFmt;
		else if (this.opts.type === 'time') one = timeFmt;
		else one = `${dateFmt} ${timeFmt}`;
		return this.range ? `${one} – ${one}` : one;
	}

	// ---- open / close -------------------------------------------------------------

	openPanel() {
		if (this.open) return;
		this.open = true;
		this.wrap.classList.add('is-open');
		this.panel.hidden = false;
		// draft state: edit a copy, commit on Apply/pick
		this.draftStart = this.start;
		this.draftEnd = this.end;
		this.viewMode = 'days';
		this.pending = false;
		this.hoverDay = null;
		if (this.hasDate && this.start instanceof Date) this.viewDate = dayOnly(this.start);
		this.render();
		// flip to right-alignment when the panel would leave the viewport
		const rect = this.wrap.getBoundingClientRect();
		const width = this.panel.offsetWidth;
		this.panel.classList.toggle('dsel-panel-right', rect.left + width > window.innerWidth - 16 && rect.right - width >= 16);
		document.addEventListener('mousedown', this.onDocClick);
		this.emit('dsel:open');
	}

	closePanel() {
		if (!this.open) return;
		this.open = false;
		this.wrap.classList.remove('is-open');
		this.panel.hidden = true;
		document.removeEventListener('mousedown', this.onDocClick);
		this.emit('dsel:close');
	}

	// ---- rendering ------------------------------------------------------------------

	render() {
		const parts = [];
		if (this.hasDate) {
			if (this.viewMode === 'months') parts.push(this.renderMonths());
			else if (this.viewMode === 'years') parts.push(this.renderYears());
			else parts.push(this.renderCalendar());
		}
		// times + footer only in the day view (month/year picking is transient)
		if (this.viewMode === 'days') {
			if (this.hasTime) parts.push(this.renderTimes());
			parts.push(this.renderFooter());
		}
		this.panel.innerHTML = parts.join('');
		this.bindPanel();
	}

	renderCalendar() {
		// per-instance option wins; otherwise the user's g.settings.weekStart
		const weekStart = this.opts.weekStart === null || this.opts.weekStart === undefined ? setting('weekStart', 1) : this.opts.weekStart;
		const names = weekdayNames(lang(), weekStart).map((n) => `<span class="dsel-wd">${n}</span>`).join('');
		const first = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
		const lead = (first.getDay() - weekStart + 7) % 7;
		const startCell = new Date(first.getFullYear(), first.getMonth(), 1 - lead);

		const selStart = this.hasDate && this.draftStart instanceof Date ? dayOnly(this.draftStart) : null;
		const selEnd = this.hasDate && this.draftEnd instanceof Date ? dayOnly(this.draftEnd) : null;
		// while picking a range, preview up to the hovered day
		const previewEnd = this.pending && this.hoverDay ? this.hoverDay : selEnd;
		const lo = selStart && previewEnd ? (selStart <= previewEnd ? selStart : previewEnd) : null;
		const hi = selStart && previewEnd ? (selStart <= previewEnd ? previewEnd : selStart) : null;
		const today = dayOnly(new Date());

		let cells = '';
		for (let i = 0; i < 42; i++) {
			const d = new Date(startCell.getFullYear(), startCell.getMonth(), startCell.getDate() + i);
			const classes = ['dsel-day'];
			if (d.getMonth() !== this.viewDate.getMonth()) classes.push('is-outside');
			if (sameDay(d, today)) classes.push('is-today');
			if ((this.minDate && d < this.minDate) || (this.maxDate && d > this.maxDate)) classes.push('is-disabled');
			if (sameDay(d, selStart) || sameDay(d, selEnd)) classes.push('is-selected');
			if (this.range && lo && hi && d > lo && d < hi) classes.push('is-in-range');
			cells += `<button type="button" class="${classes.join(' ')}" data-day="${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}">${d.getDate()}</button>`;
		}

		return `<div class="dsel-cal"><div class="dsel-cal-head"><button type="button" class="dsel-nav" data-nav="-1" aria-label="${t('Previous month')}"><i class="ri-arrow-left-s-line" aria-hidden="true"></i></button><button type="button" class="dsel-cal-label" data-view="months" aria-label="${t('Pick month and year')}">${monthLabel(this.viewDate, lang())}</button><button type="button" class="dsel-nav" data-nav="1" aria-label="${t('Next month')}"><i class="ri-arrow-right-s-line" aria-hidden="true"></i></button></div><div class="dsel-grid dsel-weekdays">${names}</div><div class="dsel-grid">${cells}</div></div>`;
	}

	renderMonths() {
		const year = this.viewDate.getFullYear();
		const current = this.draftStart instanceof Date ? this.draftStart : null;
		let cells = '';
		monthNamesShort(lang()).forEach((name, i) => {
			const classes = ['dsel-month'];
			if (current && current.getFullYear() === year && current.getMonth() === i) classes.push('is-selected');
			cells += `<button type="button" class="${classes.join(' ')}" data-month="${i}">${name}</button>`;
		});
		return `<div class="dsel-cal"><div class="dsel-cal-head"><button type="button" class="dsel-nav" data-ynav="-1" aria-label="${t('Previous year')}"><i class="ri-arrow-left-s-line" aria-hidden="true"></i></button><button type="button" class="dsel-cal-label" data-view="years" aria-label="${t('Pick year')}">${year}</button><button type="button" class="dsel-nav" data-ynav="1" aria-label="${t('Next year')}"><i class="ri-arrow-right-s-line" aria-hidden="true"></i></button></div><div class="dsel-months">${cells}</div></div>`;
	}

	renderYears() {
		const base = Math.floor(this.viewDate.getFullYear() / 12) * 12;
		const current = this.draftStart instanceof Date ? this.draftStart.getFullYear() : null;
		let cells = '';
		for (let y = base; y < base + 12; y++) {
			const classes = ['dsel-month'];
			if (y === current) classes.push('is-selected');
			cells += `<button type="button" class="${classes.join(' ')}" data-year="${y}">${y}</button>`;
		}
		return `<div class="dsel-cal"><div class="dsel-cal-head"><button type="button" class="dsel-nav" data-ynav="-12" aria-label="${t('Previous years')}"><i class="ri-arrow-left-s-line" aria-hidden="true"></i></button><span class="dsel-cal-label is-static">${base}–${base + 11}</span><button type="button" class="dsel-nav" data-ynav="12" aria-label="${t('Next years')}"><i class="ri-arrow-right-s-line" aria-hidden="true"></i></button></div><div class="dsel-months">${cells}</div></div>`;
	}

	renderTimes() {
		const timeFmt = this.opts.timeFormat || setting('timeFormat', 'HH:mm');
		const is12h = timeFmt.includes('A'); // 12h clock: hours 1-12 + AM/PM
		const timeOf = (v) => (v instanceof Date ? { h: v.getHours(), m: v.getMinutes() } : (v || { h: 0, m: 0 }));

		const row = (slot, label, value) => {
			const tv = timeOf(value);
			let hours = '';
			if (is12h) {
				const h12 = tv.h % 12 || 12;
				for (let i = 1; i <= 12; i++) hours += `<option value="${i}" ${i === h12 ? 'selected' : ''}>${pad(i)}</option>`;
			} else {
				for (let i = 0; i < 24; i++) hours += `<option value="${i}" ${i === tv.h ? 'selected' : ''}>${pad(i)}</option>`;
			}
			let minutes = '';
			for (let i = 0; i < 60; i += this.opts.minuteStep) minutes += `<option value="${i}" ${i === tv.m ? 'selected' : ''}>${pad(i)}</option>`;
			const select = (unit, options, aria) => `<select class="form-select form-select-sm dsel-time" data-slot="${slot}" data-unit="${unit}" aria-label="${aria}">${options}</select>`;
			const meridiem = is12h ? select('ap', `<option value="am" ${tv.h < 12 ? 'selected' : ''}>AM</option><option value="pm" ${tv.h >= 12 ? 'selected' : ''}>PM</option>`, t('AM/PM')) : '';
			return `<div class="dsel-time-row">${label ? `<span class="dsel-time-label">${label}</span>` : ''}<span class="dsel-time-pair">${select('h', hours, t('Hours'))}<span class="dsel-time-sep">:</span>${select('m', minutes, t('Minutes'))}${meridiem}</span></div>`;
		};

		const rows = this.range
			? row('start', t('From'), this.draftStart) + row('end', t('To'), this.draftEnd)
			: row('start', '', this.draftStart);
		return `<div class="dsel-times">${rows}</div>`;
	}

	renderFooter() {
		const nowLabel = this.hasDate ? t('Today') : t('Now');
		// plain date picks commit on click; everything else commits on Apply
		const needsApply = this.hasTime || this.range;
		return `<div class="dsel-foot"><button type="button" class="btn btn-sm btn-secondary dsel-now">${nowLabel}</button>${needsApply ? `<button type="button" class="btn btn-sm btn-primary dsel-apply">${t('Apply')}</button>` : ''}</div>`;
	}

	bindPanel() {
		this.panel.querySelectorAll('[data-nav]').forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + parseInt(btn.getAttribute('data-nav'), 10), 1);
				this.render();
			});
		});

		this.panel.querySelectorAll('.dsel-day').forEach((btn) => {
			btn.addEventListener('click', () => {
				if (btn.classList.contains('is-disabled')) return;
				this.pickDay(btn.getAttribute('data-day'));
			});
			if (this.range) {
				btn.addEventListener('mouseover', () => {
					if (!this.pending) return;
					const [y, m, d] = btn.getAttribute('data-day').split('-').map(Number);
					const hovered = new Date(y, m - 1, d);
					if (this.hoverDay && sameDay(this.hoverDay, hovered)) return; // no re-render loop
					this.hoverDay = hovered;
					this.render();
				});
			}
		});

		this.panel.querySelectorAll('.dsel-time').forEach((sel) => {
			sel.addEventListener('change', () => {
				const slot = sel.getAttribute('data-slot');
				const rowEl = sel.closest('.dsel-time-row');
				const part = (unit) => rowEl.querySelector(`[data-unit="${unit}"]`);
				const meridiem = part('ap');
				let hours = parseInt(part('h').value, 10);
				if (meridiem) hours = (hours % 12) + (meridiem.value === 'pm' ? 12 : 0);
				const key = slot === 'start' ? 'draftStart' : 'draftEnd';
				this[key] = this.withTime(this.withTime(this[key], 'h', hours), 'm', parseInt(part('m').value, 10));
			});
		});

		// month/year drill-up navigation
		const label = this.panel.querySelector('.dsel-cal-label[data-view]');
		if (label) label.addEventListener('click', () => { this.viewMode = label.getAttribute('data-view'); this.render(); });
		this.panel.querySelectorAll('[data-ynav]').forEach((btn) => {
			btn.addEventListener('click', () => {
				this.viewDate = new Date(this.viewDate.getFullYear() + parseInt(btn.getAttribute('data-ynav'), 10), this.viewDate.getMonth(), 1);
				this.render();
			});
		});
		this.panel.querySelectorAll('[data-month]').forEach((btn) => {
			btn.addEventListener('click', () => {
				this.viewDate = new Date(this.viewDate.getFullYear(), parseInt(btn.getAttribute('data-month'), 10), 1);
				this.viewMode = 'days';
				this.render();
			});
		});
		this.panel.querySelectorAll('[data-year]').forEach((btn) => {
			btn.addEventListener('click', () => {
				this.viewDate = new Date(parseInt(btn.getAttribute('data-year'), 10), this.viewDate.getMonth(), 1);
				this.viewMode = 'months';
				this.render();
			});
		});

		const now = this.panel.querySelector('.dsel-now');
		if (now) now.addEventListener('click', () => {
			const current = new Date();
			if (this.opts.type === 'date') {
				this.draftStart = dayOnly(current);
				if (this.range) { this.draftEnd = dayOnly(current); }
				this.commit();
				this.closePanel();
				return;
			}
			if (this.hasDate) this.viewDate = dayOnly(current);
			const timeNow = { h: current.getHours(), m: current.getMinutes() };
			this.draftStart = this.hasDate ? current : timeNow;
			if (this.range && !this.draftEnd) this.draftEnd = this.hasDate ? current : { ...timeNow };
			this.render();
		});

		const apply = this.panel.querySelector('.dsel-apply');
		if (apply) apply.addEventListener('click', () => {
			this.commitDraftDefaults();
			this.commit();
			this.closePanel();
		});
	}

	// ---- picking ------------------------------------------------------------------------

	withTime(value, unit, amount) {
		if (this.hasDate) {
			const base = value instanceof Date ? new Date(value) : new Date(this.viewDate);
			if (unit === 'h') base.setHours(amount);
			else base.setMinutes(amount);
			base.setSeconds(0);
			return base;
		}
		const time = value ? { ...value } : { h: 0, m: 0 };
		time[unit] = amount;
		return time;
	}

	pickDay(dayString) {
		const [y, m, d] = dayString.split('-').map(Number);
		const keepTime = (from) => {
			const date = new Date(y, m - 1, d);
			if (this.hasTime && from instanceof Date) { date.setHours(from.getHours(), from.getMinutes(), 0); }
			return date;
		};

		if (!this.range) {
			this.draftStart = keepTime(this.draftStart);
			if (!this.hasTime) { this.commit(); this.closePanel(); return; }
			this.render();
			return;
		}

		if (!this.pending) {
			this.draftStart = keepTime(this.draftStart);
			this.draftEnd = null;
			this.pending = true;
			this.render();
			return;
		}

		let end = keepTime(this.draftEnd);
		this.pending = false;
		if (dayOnly(end) < dayOnly(this.draftStart)) { const swap = this.draftStart; this.draftStart = end; end = swap; }
		this.draftEnd = end;
		if (!this.hasTime) { this.commit(); this.closePanel(); return; }
		this.render();
	}

	// Fill whatever the user did not touch before Apply.
	commitDraftDefaults() {
		if (this.hasDate) {
			if (!(this.draftStart instanceof Date)) this.draftStart = new Date(this.viewDate);
			if (this.range && !(this.draftEnd instanceof Date)) this.draftEnd = new Date(this.draftStart);
			if (this.range && dayOnly(this.draftEnd) < dayOnly(this.draftStart)) { const s = this.draftStart; this.draftStart = this.draftEnd; this.draftEnd = s; }
		} else {
			if (!this.draftStart) this.draftStart = { h: 0, m: 0 };
			if (this.range && !this.draftEnd) this.draftEnd = { ...this.draftStart };
		}
	}

	commit() {
		this.start = this.draftStart;
		this.end = this.range ? this.draftEnd : null;
		this.syncHolder(true);
	}

	// ---- value <-> holder -------------------------------------------------------------------

	pg(value) {
		if (!value && value !== 0) return '';
		return this.hasDate ? toPgTimestamp(value) : toPgTime(value);
	}

	displayOf(value) {
		if (!value) return '';
		const dateFmt = this.opts.dateFormat || setting('dateFormat', 'DD.MM.YYYY');
		const timeFmt = this.opts.timeFormat || setting('timeFormat', 'HH:mm');
		if (this.opts.type === 'date') return formatDisplay(dateFmt, value);
		if (this.opts.type === 'time') return formatTimeDisplay(timeFmt, value);
		return formatDisplay(`${dateFmt} ${timeFmt}`, value);
	}

	signature() {
		return `${this.start ? this.pg(this.start) : ''}\x1f${this.end ? this.pg(this.end) : ''}`;
	}

	syncHolder(fireChange) {
		this.el.value = this.start ? this.pg(this.start) : '';
		if (this.range) this.endInput.value = this.end ? this.pg(this.end) : '';
		const startText = this.displayOf(this.start);
		this.display.value = this.range && this.end ? `${startText} – ${this.displayOf(this.end)}` : startText;
		this.clearBtn.hidden = !(this.opts.clearable && this.start && !this.readonly && !this.el.disabled);
		this.wrap.classList.toggle('has-value', !!this.start);

		const signature = this.signature();
		if (fireChange && signature !== this.lastFired) {
			this.lastFired = signature;
			this.el.dispatchEvent(new Event('change', { bubbles: true }));
			this.emit('dsel:change', { value: this.getValue() });
		}
	}

	emit(name, detail) {
		this.el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { dateSelekt: this, ...detail } }));
	}

	// ---- public API -----------------------------------------------------------------------------

	// PG string (single) or { start, end } (range); null/empty when unset.
	getValue() {
		if (!this.range) return this.start ? this.pg(this.start) : null;
		return { start: this.start ? this.pg(this.start) : null, end: this.end ? this.pg(this.end) : null };
	}

	// Accepts PG strings or Date objects; ranges: (start, end), { start, end }
	// or 'start,end'. min/max do NOT apply here - code can set anything.
	setValue(start, end) {
		if (start && typeof start === 'object' && !(start instanceof Date)) { end = start.end; start = start.start; }
		if (typeof start === 'string' && this.range && start.includes(',') && end === undefined) [start, end] = start.split(',').map((s) => s.trim());
		const parse = (v) => {
			if (!v) return null;
			let out;
			if (v instanceof Date) out = this.hasDate ? v : { h: v.getHours(), m: v.getMinutes() };
			else out = this.hasDate ? parsePgTimestamp(v) : parsePgTime(v);
			// plain dates carry no time-of-day
			if (this.opts.type === 'date' && out instanceof Date) out = dayOnly(out);
			return out;
		};
		this.start = parse(start);
		this.end = this.range ? parse(end) : null;
		if (this.start instanceof Date) this.viewDate = dayOnly(this.start);
		this.syncHolder(true);
	}

	clear() {
		if (!this.start && !this.end) return;
		this.start = null;
		this.end = null;
		this.syncHolder(true);
	}

	// Re-render the display (e.g. after changing g.settings.dateFormat).
	refresh() {
		this.display.placeholder = this.opts.placeholder || this.displayPattern();
		this.syncHolder(false);
		if (this.open) this.render();
	}

	destroy() {
		document.removeEventListener('mousedown', this.onDocClick);
		this.wrap.parentNode.insertBefore(this.el, this.wrap);
		if (this.range) this.endInput.remove();
		this.wrap.remove();
		this.el.classList.remove('dsel-holder');
		this.el.removeAttribute('aria-hidden');
		this.el.tabIndex = 0;
		delete this.el.dateSelekt;
	}
}
